import { useEffect, useRef, useState, useMemo } from 'react'
import {
  getOwnerIdFromSettings,
  Mtds,
  SampleStatusEnum,
  ScreenHeader,
  TopBar,
  AppLayout,
  useAvdeling,
  useFilters,
  FilterDrawer,
  useSampleIds,
  SamplesView,
  EmptyState,
  getEmptySampleFilters,
  getFilterCount,
  SampleFilterForm
} from 'prove-common'
import { FilterTags } from '../components/filter-tags/FilterTags'
import { useSamples } from '../hooks/useSamples'
import { AppLogo } from '../components/app-logo/AppLogo'
import { useSearchParams } from 'react-router'
import { SideBar } from '../components/side-bar/SideBar'
import type { Sample } from 'prove-common'

type FetchNextPage = () => Promise<unknown>

export const getLoaderContent = (hasNextPage: boolean) =>
  hasNextPage ? (
    <Mtds.Flex data-justify={'center'}>
      <Mtds.Spinner>Laster inn flere prøver...</Mtds.Spinner>
    </Mtds.Flex>
  ) : (
    'Ingen flere prøver å laste inn'
  )

function deriveCurrentContext(
  selectedTabIndex: number,
  isSearchMode: boolean,
  samplesOwnerId: ReturnType<typeof useSamples>,
  samplesUserId: ReturnType<typeof useSamples>,
  searchSamplesResult: ReturnType<typeof useSamples>
) {
  if (isSearchMode) {
    return {
      isSearchMode,
      samples: searchSamplesResult.filteredSamples,
      pending: searchSamplesResult.isPending,
      isFetching: searchSamplesResult.isFetching,
      isFetchingNextPage: searchSamplesResult.isFetchingNextPage,
      hasNextPage: searchSamplesResult.hasNextPage,
      fetchNextPage: searchSamplesResult.fetchNextPage
    }
  }
  const source = selectedTabIndex === 0 ? samplesOwnerId : samplesUserId
  return {
    isSearchMode,
    samples: source.filteredSamples,
    pending: source.isPending,
    isFetching: source.isFetching,
    isFetchingNextPage: source.isFetchingNextPage,
    hasNextPage: source.hasNextPage,
    fetchNextPage: source.fetchNextPage
  }
}

function getEmptyStateContent(
  ctx: { isSearchMode: boolean },
  isFiltering: boolean,
  selectedTabIndex: number
) {
  if (ctx.isSearchMode) return { title: 'Ingen treff' }
  if (isFiltering)
    return { title: 'Ingen treff', description: 'Prøv andre filtervalg.' }
  return {
    title: 'Ingen prøver',
    description:
      selectedTabIndex === 0
        ? 'Ingen prøver å vise for denne avdelingen.'
        : 'Ingen prøver å vise for deg.'
  }
}

const skeletonKeys = Array.from(
  { length: 6 },
  (_, i) => `samples-skeleton-${i}`
)

export const isSampleIdSearchValue = (value: string) => {
  const trimmed = value.trim()
  return /^[^,\s]+(\s*,\s*[^,\s]+)*\s*,?$/.test(trimmed)
}

function SamplesCardListView({
  samples,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  loaderContent
}: Readonly<{
  samples: Sample[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: FetchNextPage
  loaderContent: React.ReactNode
}>) {
  const loaderRef = useRef<HTMLDivElement>(null)
  const fetchTriggeredRef = useRef(false)

  useEffect(() => {
    if (!isFetchingNextPage) {
      fetchTriggeredRef.current = false
    }
  }, [isFetchingNextPage, samples.length])

  useEffect(() => {
    const loader = loaderRef.current
    if (loader == null || !hasNextPage) return

    const observer = new IntersectionObserver((entries) => {
      if (
        entries.some((entry) => entry.isIntersecting) &&
        !isFetchingNextPage &&
        !fetchTriggeredRef.current
      ) {
        fetchTriggeredRef.current = true
        fetchNextPage().catch((error: unknown) => {
          fetchTriggeredRef.current = false
          console.error(error)
        })
      }
    })

    observer.observe(loader)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <>
      <SamplesView mode='card' samples={samples} />
      <div ref={loaderRef} className='mt-2'>
        {loaderContent}
      </div>
    </>
  )
}

const SamplesScreen = () => {
  const settings = useAvdeling()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabIndexParam = searchParams.get('tab')

  // Initialize selectedTabIndex from searchParams or fallback to localStorage or 0
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    const savedTabIndex = localStorage.getItem('localSettings.samples.tabIndex')
    if (tabIndexParam) return Number(tabIndexParam)
    if (savedTabIndex) return Number(savedTabIndex)
    return 0
  })

  // Persist selectedTabIndex to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      'localSettings.samples.tabIndex',
      selectedTabIndex.toString()
    )
  }, [selectedTabIndex])

  const { filters, setFilters, isFiltering } =
    useFilters<SampleStatusEnum>('filterTakeSamples')
  const [isFilterClicked, setIsFilterClicked] = useState(false)
  const [isFilterReady, setIsFilterReady] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const parsedSampleIds = useSampleIds(searchValue)
  const sampleIds = useMemo(
    () => (isSampleIdSearchValue(searchValue) ? parsedSampleIds : []),
    [parsedSampleIds, searchValue]
  )

  const settingsReady = !settings.isPending

  // Memoized filter objects to stabilize object identity
  const searchFilters = useMemo(
    () => ({
      sampleIds
    }),
    [sampleIds]
  )
  const ownerId = getOwnerIdFromSettings(settings.get)
  const ownerFilters = useMemo(
    () => ({
      ...filters,
      ownerId
    }),
    [filters, ownerId]
  )
  const userFilters = useMemo(
    () => ({
      ...filters,
      userId: 'self'
    }),
    [filters]
  )

  const isSearchMode = settingsReady && sampleIds.length > 0

  const searchSamplesResult = useSamples({
    filters: searchFilters,
    activePlan: undefined,
    isEnabled: isSearchMode
  })
  const samplesOwnerId = useSamples({
    filters: ownerFilters,
    activePlan: true,
    isEnabled: settingsReady
  })
  const samplesUserId = useSamples({
    filters: userFilters,
    activePlan: true,
    isEnabled: settingsReady
  })

  const isError = samplesOwnerId.isError || samplesUserId.isError

  const ctx = deriveCurrentContext(
    selectedTabIndex,
    isSearchMode,
    samplesOwnerId,
    samplesUserId,
    searchSamplesResult
  )
  const currentLoaderContent = getLoaderContent(ctx.hasNextPage)
  const samplesViewKey = isSearchMode
    ? `search:${sampleIds.join(',')}`
    : `tab:${selectedTabIndex}`
  const showEmptyState =
    !ctx.pending && Array.isArray(ctx.samples) && ctx.samples.length === 0
  const emptyStateContent = getEmptyStateContent(
    ctx,
    isFiltering,
    selectedTabIndex
  )

  useEffect(() => {
    setSearchParams({ tab: selectedTabIndex.toString() }, { replace: true })
  }, [selectedTabIndex])

  const filterCount = getFilterCount(filters)
  const sampleHitCount =
    selectedTabIndex === 0
      ? samplesOwnerId.totalElements
      : samplesUserId.totalElements

  const resetFilters = () => {
    setFilters(getEmptySampleFilters())
  }

  const renderCount = (isPending: boolean, count: number | undefined) => {
    if (isPending)
      return (
        <span className='inline-block w-4 align-middle'>
          <Mtds.Skeleton />
        </span>
      )
    if (count == null) return null
    return <> ({count})</>
  }

  return (
    <AppLayout
      sidebar={<SideBar />}
      isLoading={!isError && settings.isPending}
      isError={isError}
      topBar={<TopBar logo={<AppLogo />} title={'Prøver'} />}
    >
      <ScreenHeader
        title='Søk etter prøver'
        description={
          <p>
            Her finner du en oversikt over alle prøver på aktive planer som er
            registrert av deg eller {samplesOwnerId.ownerName?.name}. Bruk
            filterfunksjonen for å avgrense listen, eller benytt søkefeltet til
            å finne en bestemt prøve. Søk etter posenummer adskilt med komma.
            Resultater vises fortløpende mens du skriver.
          </p>
        }
      />
      <Mtds.Grid data-gap='4'>
        <Mtds.Input
          type='search'
          aria-label='Søk på posenummer'
          placeholder='Søk på posenummer'
          value={searchValue}
          onChange={({ target }) => setSearchValue(target.value)}
        />

        {!isSearchMode && (
          <>
            <Mtds.Flex data-justify={'space-between'}>
              <Mtds.Togglegroup
                {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
                aria-label='Eier av prøver'
              >
                <Mtds.Togglegroup.Item
                  checked={selectedTabIndex === 0}
                  name='samples-tab-filter'
                  value='avdeling'
                  onChange={() => setSelectedTabIndex(0)}
                >
                  {samplesOwnerId.ownerName?.type}
                  {renderCount(
                    samplesOwnerId.isPending,
                    samplesOwnerId.totalElements
                  )}
                </Mtds.Togglegroup.Item>
                <Mtds.Togglegroup.Item
                  checked={selectedTabIndex === 1}
                  name='samples-tab-filter'
                  value='deg'
                  onChange={() => setSelectedTabIndex(1)}
                >
                  Deg
                  {renderCount(
                    samplesUserId.isPending,
                    samplesUserId.totalElements
                  )}
                </Mtds.Togglegroup.Item>
              </Mtds.Togglegroup>
              <FilterDrawer
                title='Filtrer prøver'
                isFiltering={isFiltering}
                applyLabel={`Vis ${sampleHitCount ?? 0} treff`}
                isApplyLoading={ctx.pending || ctx.isFetching}
                showApply={isFilterReady}
                filterCount={filterCount}
                onReset={resetFilters}
                onOpen={() => setIsFilterClicked(true)}
              >
                <SampleFilterForm
                  filters={filters}
                  setFilters={setFilters}
                  isEnabled={isFilterClicked}
                  onReadyChange={setIsFilterReady}
                />
              </FilterDrawer>
            </Mtds.Flex>
            <FilterTags
              filters={filters}
              setFilters={setFilters}
              isFiltering={isFiltering}
            />
          </>
        )}
      </Mtds.Grid>
      <div>
        {ctx.pending && (
          <Mtds.Grid data-gap={2} aria-busy='true'>
            {skeletonKeys.map((key) => (
              <Mtds.Skeleton key={key} className='h-14 w-full' />
            ))}
          </Mtds.Grid>
        )}
        {!ctx.pending &&
          Array.isArray(ctx.samples) &&
          ctx.samples.length > 0 && (
            <SamplesCardListView
              key={samplesViewKey}
              samples={ctx.samples}
              hasNextPage={ctx.hasNextPage}
              isFetchingNextPage={ctx.isFetchingNextPage}
              fetchNextPage={ctx.fetchNextPage}
              loaderContent={currentLoaderContent}
            />
          )}
        {showEmptyState && <EmptyState {...emptyStateContent} />}
      </div>
    </AppLayout>
  )
}

export default SamplesScreen
